import * as pdfjsLib from "pdfjs-dist";
import type { PageText } from "../storage/types";
import { checkExtractedText, checkPdfBytes, MAX_PDF_PAGES } from "./validateFile";

if (typeof window !== "undefined") {
  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.min.mjs",
    import.meta.url
  ).toString();
}

export type ExtractResult =
  | { ok: true; pages: PageText[]; text: string }
  | { ok: false; error: string };

interface TextItem {
  str: string;
}

export async function extractTextFromPdf(file: File): Promise<ExtractResult> {
  try {
    const buffer = await file.arrayBuffer();

    // Checked before pdf.js is handed the bytes. Everything past this point
    // is a parser being fed a file a stranger chose, so the cheap rejections
    // happen first.
    const verdict = checkPdfBytes(new Uint8Array(buffer));
    if (!verdict.ok) return { ok: false, error: verdict.error };

    const doc = await pdfjsLib.getDocument({
      data: buffer,
      // A PDF is a container format that can carry XFA forms, embedded
      // files and fonts. None of that is wanted: Syft needs the text and
      // nothing else, so the surface is switched off rather than relied on
      // to be off by default. (pdf.js removed its eval-based font path in
      // v6, so there is no isEvalSupported to set any more.)
      enableXfa: false,
      disableFontFace: true,
    }).promise;

    if (doc.numPages > MAX_PDF_PAGES) {
      return {
        ok: false,
        error: `That PDF has ${doc.numPages} pages. The limit is ${MAX_PDF_PAGES}.`,
      };
    }

    const pages: PageText[] = [];
    for (let i = 1; i <= doc.numPages; i++) {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const text = (content.items as TextItem[])
        .map((item) => ("str" in item ? item.str : ""))
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      pages.push({ pageNumber: i, text });
    }

    const text = pages
      .map((p) => p.text)
      .filter((t) => t.length > 0)
      .join("\n\n");

    // Not just "is it empty". A title page that scanned as text in front of
    // forty pages of images extracts fine and is still useless, and used to
    // fail much later with a message about pasting more text.
    const enough = checkExtractedText(text, doc.numPages);
    if (!enough.ok) return { ok: false, error: enough.error };

    return { ok: true, pages, text };
  } catch (err) {
    // Surface the underlying pdf.js reason. A bare "failed to read" message is
    // undiagnosable for both the student and us.
    const reason = err instanceof Error ? `${err.name}: ${err.message}` : String(err);

    // pdf.js signals an encrypted document by name. "PasswordException: No
    // password given" is accurate and tells the student nothing.
    if (err instanceof Error && err.name === "PasswordException") {
      return {
        ok: false,
        error: "That PDF is password protected. Remove the password, or paste the text instead.",
      };
    }

    console.error("PDF extraction failed", err);
    return {
      ok: false,
      error: `Failed to read this PDF (${reason}). Try to paste the text instead.`,
    };
  }
}
